
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const Invite = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    const validateInvitation = async () => {
      const email = searchParams.get("email");
      const orgId = searchParams.get("org");

      console.log("🔍 Invite validation - URL params:", { email, orgId });
      console.log("🔍 Full search params:", searchParams.toString());

      if (!email || !orgId) {
        console.error("❌ Missing parameters:", { email, orgId });
        toast.error("Invalid invitation link");
        navigate("/");
        return;
      }

      try {
        console.log("🔍 Searching for invitation:", { email, orgId });
        const { data: invitation, error } = await supabase
          .from('user_invitations')
          .select('*')
          .eq('email', email)
          .eq('organization_id', orgId)
          .eq('status', 'pending')
          .gte('expires_at', new Date().toISOString())
          .maybeSingle();

        console.log("🔍 Invitation query result:", { invitation, error });

        if (error) throw error;

        if (!invitation) {
          // Specifically check if invitation has expired
          const { data: expiredInvite, error: expiredError } = await supabase
            .from('user_invitations')
            .select('expires_at')
            .eq('email', email)
            .eq('organization_id', orgId)
            .eq('status', 'pending')
            .lt('expires_at', new Date().toISOString())
            .maybeSingle();

          if (expiredError) throw expiredError;

          if (expiredInvite) {
            toast.error("This invitation has expired. Please request a new invitation.");
          } else {
            toast.error("Invalid invitation");
          }
          
          navigate("/");
          return;
        }

        // Redirect to branded token-based invite page for consistent styling
        navigate(`/invite-contact?token=${invitation.id}`);
        setIsValidating(false);
        return;

      } catch (error) {
        console.error("Error validating invitation:", error);
        toast.error("Failed to validate invitation");
        navigate("/");
      } finally {
        setIsValidating(false);
      }
    };

    validateInvitation();
  }, [navigate, searchParams]);

  const handleInvitationAcceptance = async (userId: string, invitation: any) => {
    try {
      const isPartnerInvitation = invitation.invited_role === 'partner';
      
      // Create user role for the organization
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert([{
          user_id: userId,
          organization_id: invitation.organization_id,
          role: invitation.invited_role || 'standard'
        }]);

      if (roleError) throw roleError;

      // Handle partner-specific setup
      if (isPartnerInvitation) {
        // Find the partner record by email and link it to the user
        const { error: partnerUpdateError } = await supabase
          .from('crm_partners')
          .update({
            user_id: userId,
            portal_access_enabled: true,
            invitation_accepted_at: new Date().toISOString()
          })
          .eq('email', invitation.email);

        if (partnerUpdateError) {
          console.error("Failed to link partner:", partnerUpdateError);
          throw partnerUpdateError;
        }
      } else {
        // Assign devices to regular users if any
        if (invitation.device_ids && invitation.device_ids.length > 0) {
          const deviceAssignments = invitation.device_ids.map((deviceId: string) => ({
            user_id: userId,
            device_id: deviceId
          }));

          const { error: assignmentError } = await supabase
            .from('device_assignments')
            .insert(deviceAssignments);

          if (assignmentError) throw assignmentError;
        }
      }

      // Update invitation status to processed
      const { error: updateError } = await supabase
        .from('user_invitations')
        .update({ status: 'processed' })
        .eq('email', invitation.email)
        .eq('organization_id', invitation.organization_id);

      if (updateError) throw updateError;

      localStorage.removeItem('pendingInvitation');
      
      if (isPartnerInvitation) {
        toast.success("Partner invitation accepted successfully");
        navigate("/dashboard/partner-portal");
      } else {
        toast.success("Invitation accepted successfully");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error accepting invitation:", error);
      toast.error("Failed to process invitation");
      throw error;
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-brand-50 to-brand-100">
        <p className="text-lg text-gray-600">Validating invitation...</p>
      </div>
    );
  }

  return null;
};

export default Invite;
